/**
 * PricingPage - Lawhelper 定价页面
 * 全新设计：简洁、现代、Claude风格
 * 支持多语言 (i18n)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PricingPage.css';
import { landingTranslations, LandingTranslation } from '../i18n/landingTranslations';

// 根据运行环境动态设置 API 基础 URL
const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE_URL = isLocalhost
    ? 'http://localhost:3001'
    : (process.env.REACT_APP_API_BASE_URL || 'https://lawhelper-api-production.up.railway.app');

// 计划图标 SVG
const PlanIcon: React.FC<{ type: 'free' | 'pro' | 'max' | 'ultra' }> = ({ type }) => {
    const colors = {
        free: '#64748b',
        pro: '#6366f1',
        max: '#f59e0b',
        ultra: '#ef4444' // Red for Ultra
    };

    const iconType = type === 'ultra' ? 'max' : type;

    return (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke={colors[type]} strokeWidth="2" fill="none" />
            <circle cx="24" cy="24" r="6" fill={colors[type]} />
            {iconType !== 'free' && (
                <>
                    <line x1="24" y1="4" x2="24" y2="12" stroke={colors[type]} strokeWidth="2" />
                    <line x1="24" y1="36" x2="24" y2="44" stroke={colors[type]} strokeWidth="2" />
                    <line x1="4" y1="24" x2="12" y2="24" stroke={colors[type]} strokeWidth="2" />
                    <line x1="36" y1="24" x2="44" y2="24" stroke={colors[type]} strokeWidth="2" />
                </>
            )}
            {(iconType === 'max') && (
                <>
                    <line x1="10" y1="10" x2="16" y2="16" stroke={colors[type]} strokeWidth="2" />
                    <line x1="32" y1="32" x2="38" y2="38" stroke={colors[type]} strokeWidth="2" />
                    <line x1="38" y1="10" x2="32" y2="16" stroke={colors[type]} strokeWidth="2" />
                    <line x1="16" y1="32" x2="10" y2="38" stroke={colors[type]} strokeWidth="2" />
                </>
            )}
        </svg>
    );
};

// 打勾图标
const CheckIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface PricingPageProps {
    onClose?: () => void;
    currentLanguage?: string;
}

// Helper to get translation with fallback
const getT = (lang: string): LandingTranslation => {
    // Try exact match first
    if (landingTranslations[lang]) {
        return landingTranslations[lang];
    }
    // Try base language (e.g., 'en' from 'en-US')
    const baseLang = lang.split('-')[0];
    const matchingLang = Object.keys(landingTranslations).find(k => k.startsWith(baseLang));
    if (matchingLang) {
        return landingTranslations[matchingLang];
    }
    // Default to English
    return landingTranslations['en-US'];
};

// Check if current language is Chinese
const isChinese = (lang: string): boolean => {
    return lang.startsWith('zh');
};

const PricingPage: React.FC<PricingPageProps> = ({ onClose, currentLanguage = 'en-US' }) => {
    const navigate = useNavigate();
    const [subscribing, setSubscribing] = useState<string | null>(null);
    const [userType, setUserType] = useState<'individual' | 'enterprise'>('individual');

    const t = getT(currentLanguage);
    const isZh = isChinese(currentLanguage);

    // Localized plan data
    const individualPlans = {
        free: {
            name: 'Free',
            description: t.planFreeDesc,
            monthlyPrice: 0,
            annualPrice: 0,
            features: isZh ? [
                '每日 20 次 AI 对话',
                '基础法律问答',
                '单国家法律数据库',
                '5 个知识库文件',
                '10MB 单文件上限',
                '社区支持'
            ] : [
                '20 AI chats per day',
                'Basic legal Q&A',
                'Single country coverage',
                '5 knowledge base files',
                '10MB file size limit',
                'Community support'
            ]
        },
        pro: {
            name: 'Pro',
            description: t.planProDesc,
            monthlyPrice: 29.99,
            annualPrice: 299,
            features: isZh ? [
                'Free 版本全部功能，以及:',
                '每日 300 次 AI 对话',
                '15 次/月 AI 审查与起草',
                '50/10/5 深度研究配额',
                '50 个知识库文件',
                '30MB 单文件上限',
                '单国家法律数据库',
                '邮件支持'
            ] : [
                'Everything in Free, plus:',
                '300 AI chats per day',
                '15 AI reviews/drafts per month',
                '50/10/5 deep research quota',
                '50 knowledge base files',
                '30MB file size limit',
                'Single country coverage',
                'Email support'
            ]
        },
        max: {
            name: 'Max',
            description: t.planMaxDesc,
            monthlyPrice: 149,
            annualPrice: 1490,
            features: isZh ? [
                'Pro 版本全部功能，以及:',
                '每日 500 次 AI 对话',
                '80 次/月 AI 审查与起草',
                '200/50/30 深度研究配额',
                '200 个知识库文件',
                '100MB 单文件上限',
                '全球法律数据库 (Global Access)',
                '高峰期优先访问',
                '专属客服支持'
            ] : [
                'Everything in Pro, plus:',
                '500 AI chats per day',
                '80 AI reviews/drafts per month',
                '200/50/30 deep research quota',
                '200 knowledge base files',
                '100MB file size limit',
                'Global legal coverage',
                'Priority access during peak hours',
                'Dedicated customer support'
            ]
        },
        ultra: {
            name: 'Ultra',
            description: t.planUltraDesc,
            monthlyPrice: 499,
            annualPrice: 4990,
            features: isZh ? [
                'Max 版本全部功能，以及:',
                '每日 2000 次 AI 对话',
                '300 次/月 AI 审查与起草',
                '1000/300/100 深度研究配额',
                '1000 个知识库文件',
                '500MB 单文件上限',
                '跨境法律对比 (Comparative Access)',
                '优先技术支持'
            ] : [
                'Everything in Max, plus:',
                '2000 AI chats per day',
                '300 AI reviews/drafts per month',
                '1000/300/100 deep research quota',
                '1000 knowledge base files',
                '500MB file size limit',
                'Cross-border legal comparison',
                'Priority technical support'
            ]
        }
    };

    // Enterprise plan data
    const enterprisePlans = {
        enterprise: {
            name: 'Enterprise',
            description: isZh ? '定制化企业解决方案' : 'Custom enterprise solutions',
            monthlyPrice: null, // Contact Sales
            annualPrice: null,
            features: isZh ? [
                'Ultra 版本全部功能，以及:',
                '无限 AI 对话与高级功能',
                '5GB 单文件上限',
                'SSO 单点登录',
                '高级数据安全与合规',
                '私有化部署选项',
                '专属客户成功经理',
                'SLA 服务等级协议'
            ] : [
                'Everything in Ultra, plus:',
                'Unlimited AI chats & advanced features',
                '5GB file size limit',
                'SSO single sign-on',
                'Advanced data security & compliance',
                'Private deployment options',
                'Dedicated customer success manager',
                'SLA service agreement'
            ]
        }
    };

    const handleSubscribe = async (tierId: string) => {
        if (tierId === 'free' || tierId === 'enterprise' || tierId === 'contact') {
            if (tierId === 'enterprise' || tierId === 'contact') {
                window.location.href = 'mailto:business@lawhelper.ai';
            }
            return;
        }

        // Get user info
        const userStr = localStorage.getItem('user');
        const authToken = localStorage.getItem('auth_token');
        let userId = 'anonymous';

        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                userId = user.id || user.user_id || 'anonymous';
            } catch (e) {
                console.warn('Failed to parse user data');
            }
        }

        // Check if logged in
        if (!authToken || userId === 'anonymous') {
            alert(isZh ? '请先登录后再订阅' : 'Please log in before subscribing');
            return;
        }

        setSubscribing(tierId);
        try {
            const response = await fetch(`${API_BASE_URL}/api/billing/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': userId,
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    tier: tierId,
                    billing: 'monthly',
                }),
            });

            const data = await response.json();

            if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
            } else if (data.success) {
                alert(isZh ? '订阅成功! 功能已解锁。' : 'Subscription successful! Features unlocked.');
                window.location.reload();
            } else if (data.error) {
                alert(isZh ? `订阅失败: ${data.message || data.error}` : `Subscription failed: ${data.message || data.error}`);
            }
        } catch (error) {
            console.error('Checkout failed:', error);
            alert(isZh ? '订阅失败，请稍后重试' : 'Subscription failed. Please try again later.');
        } finally {
            setSubscribing(null);
        }
    };

    const getPrice = (plan: any) => {
        if (plan.monthlyPrice === null) return t.contactSales;
        const price = plan.monthlyPrice;
        if (price === 0) return '€0';
        return `€${price}`;
    };

    const getPriceSuffix = (plan: any) => {
        if (plan.monthlyPrice === null) return '';
        if (plan.monthlyPrice === 0) return '';
        return t.perMonth;
    };

    // Button text helpers
    const getButtonText = (key: string, planName: string) => {
        if (key === 'free') {
            return isZh ? '免费使用 Lawhelper' : 'Use Lawhelper for free';
        }
        return isZh ? `立即开通 ${planName}` : `Get ${planName} plan`;
    };

    const getFeatureListHeader = (key: string) => {
        if (key === 'free') return '';
        if (key === 'pro') {
            return isZh ? 'Free 版本全部功能，以及:' : 'Everything in Free, plus:';
        }
        return isZh ? 'Pro 版本全部功能，以及:' : 'Everything in Pro, plus:';
    };

    return (
        <div className="pricing-page-new">
            {/* Back button - 始终显示 */}
            <button
                className="pricing-close"
                onClick={() => onClose ? onClose() : navigate('/')}
                aria-label="Go back"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {/* Header */}
            <header className="pricing-header-new">
                <h1>{isZh ? '选择适合您的方案' : 'Plans that grow with you'}</h1>

                {/* User type toggle */}
                <div className="user-type-toggle">
                    <button
                        className={userType === 'individual' ? 'active' : ''}
                        onClick={() => setUserType('individual')}
                    >
                        {isZh ? '个人' : 'Individual'}
                    </button>
                    <button
                        className={userType === 'enterprise' ? 'active' : ''}
                        onClick={() => setUserType('enterprise')}
                    >
                        {isZh ? '团队与企业' : 'Team & Enterprise'}
                    </button>
                </div>
            </header>

            {/* Pricing cards container */}
            <div className="pricing-cards-container">

                {/* Pricing cards grid */}
                <div className={`pricing-grid-new ${userType === 'enterprise' ? 'enterprise-grid' : ''}`}>
                    {userType === 'individual' ? (
                        <>
                            {/* Individual Plans */}
                            {Object.entries(individualPlans).map(([key, plan]) => (
                                <div key={key} className={`pricing-card-new ${key}`}>
                                    <PlanIcon type={key as any} />
                                    <h2>{plan.name}</h2>
                                    <p className="plan-description">{plan.description}</p>
                                    <div className="plan-price">
                                        {plan.name === 'Max' && <span className="price-label">From </span>}
                                        <span className="price-amount">{getPrice(plan)}</span>
                                        <span className="price-suffix">{getPriceSuffix(plan)}</span>
                                    </div>
                                    <button
                                        className={`plan-button ${key}`}
                                        onClick={() => handleSubscribe(key)}
                                        disabled={subscribing === key}
                                    >
                                        {subscribing === key ? (isZh ? '处理中...' : 'Processing...') : getButtonText(key, plan.name)}
                                    </button>
                                    <div className="feature-list-header">
                                        {key === 'free' ? '' : key === 'pro' ? (isZh ? 'Free 版本全部功能，以及:' : 'Everything in Free and:') : (isZh ? 'Pro 版本全部功能，以及:' : 'Everything in Pro, plus:')}
                                    </div>
                                    <ul className="feature-list">
                                        {plan.features.map((feature, index) => (
                                            <li key={index}>
                                                <CheckIcon />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            {/* Enterprise Plans */}
                            {Object.entries(enterprisePlans).map(([key, plan]) => (
                                <div key={key} className={`pricing-card-new ${key}`}>
                                    <PlanIcon type={'max'} />
                                    <h2>{plan.name}</h2>
                                    <p className="plan-description">{plan.description}</p>
                                    <div className="plan-price">
                                        <span className="price-amount">{getPrice(plan)}</span>
                                        <span className="price-suffix">{getPriceSuffix(plan)}</span>
                                    </div>
                                    <button
                                        className="plan-button max"
                                        onClick={() => handleSubscribe('contact')}
                                    >
                                        {t.contactSales}
                                    </button>
                                    <div className="feature-list-header">
                                        {isZh ? 'Ultra 版本全部功能，以及:' : 'Everything in Ultra, plus:'}
                                    </div>
                                    <ul className="feature-list">
                                        {plan.features.map((feature, index) => (
                                            <li key={index}>
                                                <CheckIcon />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>

            {/* Footer */}
            <footer className="pricing-footer">
                <p>{isZh ? '*使用量限制适用。价格不含适用税费。' : '*Usage limits apply. Prices shown don\'t include applicable tax.'}</p>
            </footer>
        </div>
    );
};


export default PricingPage;
